import { Search } from "lucide-react";
import * as React from "react";

import type { DealDashboardMetrics, ManagerDealListItem } from "@/api/client";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  activeDealFilterCount,
  EMPTY_DEAL_FILTERS,
  matchesDealFilters,
  type DealAdvancedFilterState,
} from "../dealFilters";
import { DealAdvancedFilters } from "../DealAdvancedFilters";
import { useDealSearchDebounce } from "../useDealSearchDebounce";
import { ManagerDealsDataGrid } from "./ManagerDealsDataGrid";
import {
  MANAGER_DEAL_TAB_LABEL,
  MANAGER_DEAL_TAB_ORDER,
  managerDealMatchesTab,
  managerDealTabCount,
  type ManagerDealTab,
} from "./managerDealTabs";

const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

/** CHP-364: fixed status order for the "group by status" sort. */
const STATUS_GROUP_ORDER: ManagerDealListItem["deal_status"][] = [
  "live",
  "extension_pending",
  "pending_review",
  "win_pending_review",
  "closed_won",
  "closed_lost",
  "denied",
  "draft",
];

/** Unique, sorted non-null values for the org/owner select option lists —
 * derived from the loaded rows (PM-ruled v1 scope: no extra fetch). */
function uniqueOptions(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => v != null && v !== ""))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export interface ManagerDealsLedgerProps {
  deals: ManagerDealListItem[];
  metrics: DealDashboardMetrics | undefined;
  activeTab: ManagerDealTab;
  onTabChange: (tab: ManagerDealTab) => void;
  /** Wave-2 Operations Core: a precise single-status narrowing applied on top
   * of the active tab, set by an Operational-Pulse pulse-card deep-link
   * (?status=…). Null = no narrowing (the tab shows all its statuses). */
  statusFilter?: ManagerDealListItem["deal_status"] | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onDealClick: (dealId: string) => void;
  /** Focused by the dashboard's "Search deals" button (CHP-222). */
  searchRef?: React.RefObject<HTMLInputElement | null>;
  /** Conventions sweep review-r2: CTA shown in the table's empty state when
   * there are no deals at all (e.g. a register-on-behalf button). */
  emptyAction?: React.ReactNode;
}

/** Master Pipeline Ledger: segmented tabs + search above a unified grid.
 *
 * Same debounced-search pattern as the partner workspace (`DealWorkspace`),
 * extracted into `useDealSearchDebounce` for reuse. Clearing the search box
 * returns the view to the All Deals tab (mirrors the partner workspace
 * "returns to default tab" behaviour). Search fields per the spec: Partner
 * company, Lead BDM, Customer (client_name) — distinct from the partner-
 * side fields (deal id / client / company / owner). */
export function ManagerDealsLedger({
  deals,
  metrics,
  activeTab,
  onTabChange,
  statusFilter = null,
  searchValue,
  onSearchChange,
  onDealClick,
  searchRef,
  emptyAction,
}: ManagerDealsLedgerProps) {
  const { query, isSearching } = useDealSearchDebounce(searchValue, {
    minChars: SEARCH_MIN_CHARS,
    ms: SEARCH_DEBOUNCE_MS,
  });

  // Advanced Filters v1 (gap-build W2) — local to the ledger, composing
  // (AND) with the tab/search result below.
  const [filters, setFilters] = React.useState<DealAdvancedFilterState>(EMPTY_DEAL_FILTERS);
  // CHP-364: clickable Status header groups by status; tab change / re-click resets.
  const [groupByStatus, setGroupByStatus] = React.useState(false);

  function handleTabChange(value: string) {
    if (!value) return;
    setGroupByStatus(false);
    onSearchChange("");
    onTabChange(value as ManagerDealTab);
  }

  function handleSearchChange(value: string) {
    onSearchChange(value);
    if (value.trim() === "") onTabChange("all");
  }

  const tabOrSearchDeals = isSearching
    ? deals.filter(
        (d) =>
          (d.channel_partner_company ?? "").toLowerCase().includes(query) ||
          (d.lead_bdm_name ?? "").toLowerCase().includes(query) ||
          d.client_name.toLowerCase().includes(query),
      )
    : // Wave-2: an active pulse-card deep-link (?status=…) narrows the tab to
      // exactly that status; otherwise the tab keeps all its statuses.
      deals.filter(
        (d) =>
          managerDealMatchesTab(d, activeTab) &&
          (statusFilter === null || d.deal_status === statusFilter),
      );

  // Submitted = submission_timestamp; Closing = the ledger's Closing Date
  // (target_close_date). Org/owner narrow by exact row value.
  const filteredDeals = tabOrSearchDeals.filter((d) =>
    matchesDealFilters(filters, {
      submittedAt: d.submission_timestamp,
      closingAt: d.target_close_date,
      valueCents: d.total_contract_value,
      partnerOrg: d.channel_partner_company,
      owner: d.lead_bdm_name,
    }),
  );

  // CHP-364: group by status = stable sort by a fixed status order.
  const displayDeals = groupByStatus
    ? [...filteredDeals].sort(
        (a, b) =>
          STATUS_GROUP_ORDER.indexOf(a.deal_status) - STATUS_GROUP_ORDER.indexOf(b.deal_status),
      )
    : filteredDeals;

  // CHP-809: when a pulse-card status deep-link (?status=…) or an advanced
  // filter narrows the tab, the tab's bracket count (from unfiltered metrics)
  // no longer equals the rendered row count. Surface a "showing N of M" signal
  // so the bracket and the visible rows never silently disagree. The "M" is the
  // tab's full count from the same metrics that feed the bracket.
  const isNarrowed = !isSearching && (statusFilter !== null || activeDealFilterCount(filters) > 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={isSearching ? "" : activeTab} onValueChange={handleTabChange}>
          {/* CHP-805: these tabs are a client-side filter, not a tab widget with
              its own panels — the filtered grid is a sibling region. Label the
              control and point aria-controls at the region it filters. */}
          <TabsList aria-label="Filter deals by status" aria-controls="manager-deals-results">
            {MANAGER_DEAL_TAB_ORDER.map((tab) => (
              <TabsTrigger key={tab} value={tab} data-testid={`manager-deal-tab-${tab}`}>
                {MANAGER_DEAL_TAB_LABEL[tab]}
                {metrics ? (
                  <span className="ml-1.5 tabular-nums text-muted-foreground">
                    ({managerDealTabCount(metrics, tab)})
                  </span>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative w-full sm:w-80">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              ref={searchRef}
              type="search"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by Partner, BDM, or Customer..."
              aria-label="Search deals"
              className="pl-9"
              data-testid="manager-deals-search"
            />
          </div>
          <DealAdvancedFilters
            value={filters}
            onChange={setFilters}
            orgOptions={uniqueOptions(deals.map((d) => d.channel_partner_company))}
            ownerOptions={uniqueOptions(deals.map((d) => d.lead_bdm_name))}
          />
        </div>
      </div>

      <div id="manager-deals-results" className="mt-4">
        {isSearching && (
          <p className="mb-3 text-sm font-semibold text-muted-foreground">
            All Results ({filteredDeals.length})
          </p>
        )}
        {isNarrowed && (
          <p
            className="mb-3 text-sm font-semibold text-muted-foreground"
            data-testid="manager-deals-showing-count"
          >
            {metrics
              ? `Showing ${displayDeals.length} of ${managerDealTabCount(metrics, activeTab)}`
              : `Showing ${displayDeals.length}`}
          </p>
        )}
        <ManagerDealsDataGrid
          deals={displayDeals}
          onDealClick={onDealClick}
          emptyMessage={
            isSearching
              ? "No deals match your search."
              : deals.length === 0
                ? "No deals registered yet"
                : "No deals in this view."
          }
          emptyHint={
            !isSearching && deals.length === 0
              ? "Register a deal on behalf of a channel partner."
              : undefined
          }
          emptyAction={!isSearching && deals.length === 0 ? emptyAction : undefined}
          statusGrouped={groupByStatus}
          onToggleStatusGroup={() => setGroupByStatus((v) => !v)}
          // CHP-809: a column sort overrides status grouping — clear the toggle
          // so the Layers highlight matches the actual row ordering.
          onColumnSort={() => setGroupByStatus(false)}
        />
      </div>
    </div>
  );
}
